import {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Alert, Button, Card, Group, SimpleGrid, Text, Title} from '@mantine/core';
import {IconArrowLeft, IconAlertCircle, IconSquareRoundedX, IconTrash, IconCopy, IconRefresh} from '@tabler/icons-react';
import {showNotification} from '@mantine/notifications';

import {useExecutionService} from 'src/services';
import {ERequestStatus} from 'src/types/enum/request-status.enum';
import {ExecutionStatusBadge} from './ExecutionStatusBadge';
import {ExecutionDownloadButton} from './ExecutionDownloadButton';
import {EFilesType} from 'src/types/enum/files-type.enum';
import {formatDate} from 'src/helpers/format-date';
import {ELocalesFormats} from 'src/types/enum/locales-formats.enum';
import {userProfileActions} from 'src/store/reducers/user-reducer';
import useTranslation from 'src/i18next.config';

function formatDuration(seconds?: number): string {
    if (seconds == null || seconds <= 0) return '-';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
}

function ExecutionDetail() {
    const {id} = useParams<{id: string}>();
    const navigate = useNavigate();
    const executionService = useExecutionService();
    const userProfile = userProfileActions.useUserProfile();
    const {t} = useTranslation();

    const [execution, setExecution] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    const showUserInfo = userProfile.isAdmin || userProfile.isOrganizationManager;
    const showOrgInfo = userProfile.isAdmin;

    useEffect(() => {
        if (id) {
            executionService.getExecutionById(
                {
                    success: (res: any) => {
                        setExecution(res);
                        setLoading(false);
                    },
                    error: () => {
                        showNotification({
                            title: t('errorFetchingData'),
                            message: t('anErrorOccurredPleaseRetryLater'),
                            color: 'red',
                        });
                        setLoading(false);
                    },
                },
                id
            );
        }
    }, [id]);

    function cancelExecution() {
        if (!id) return;
        executionService.postStopExecution(
            {
                error: () => {
                    showNotification({
                        title: t('errorCancelingExecution'),
                        message: t('anErrorOccurredPleaseRetryLater'),
                        color: 'red',
                    });
                },
                success: () => {
                    showNotification({
                        title: t('stopExecutionRequestSent'),
                        message: t('stopExecutionRequestSent'),
                        color: 'green',
                    });
                    setExecution({...execution, status: ERequestStatus.CANCELED});
                },
            },
            {executionId: id}
        );
    }

    function deleteFiles() {
        if (!id) return;
        executionService.postDeleteExecutionFiles(
            {
                error: () => {
                    showNotification({
                        title: t('errorDeletingExecutionFiles'),
                        message: t('anErrorOccurredPleaseRetryLater'),
                        color: 'red',
                    });
                },
                success: () => {
                    showNotification({
                        title: t('deleteFilesRequestSent'),
                        message: t('deleteFilesRequestSent'),
                        color: 'green',
                    });
                    setExecution({
                        ...execution,
                        hasDeletedUploadFiles: true,
                        hasDeletedResultFiles: true,
                        numberOfUploadedFiles: 0,
                        weightOfUploadedFiles: 0,
                        numberOfResultedFiles: 0,
                        weightOfResultedFiles: 0,
                    });
                },
            },
            {executionId: id}
        );
    }

    function downloadClicked(execId: string, index = 0, source: string) {
        executionService.getResultsFiles(
            {
                error: () => {
                    showNotification({
                        title: t('errorFetchingData'),
                        message: t('anErrorOccurredPleaseRetryLater'),
                        color: 'red',
                    });
                },
                success: res => {
                    const data = res.data;
                    if (data === -1 || data === '-1') return;
                    const blob = new Blob([data]);
                    const link = document.createElement('a');
                    link.href = window.URL.createObjectURL(blob);
                    const filename = res.headers.get('content-disposition')?.split(';')[1]?.split('=')[1];
                    if (filename) {
                        link.download = JSON.parse(filename) || 'results';
                        link.click();
                        link.remove();
                        downloadClicked(execId, index + 1, source);
                    }
                },
            },
            {executionId: execId, index, source}
        );
    }

    function copyId() {
        const execId = execution.id || execution._id;
        if (execId) {
            navigator.clipboard.writeText(execId).then(() => {
                showNotification({
                    title: 'Copied to clipboard',
                    message: 'Execution ID copied to clipboard',
                    color: 'green',
                });
            });
        }
    }

    function rerunExecution() {
        if (!id) return;
        executionService.rerunExecution(
            {
                error: (err: any) => {
                    showNotification({
                        title: t('execution.rerunNotification'),
                        message: err?.message || t('execution.rerunErrorMessage'),
                        color: 'red',
                    });
                },
                success: (res: any) => {
                    showNotification({
                        title: t('execution.rerunNotification'),
                        message: t('execution.rerunSuccessMessage'),
                        color: 'green',
                    });
                    navigate(`/executions/${res.executionRequestId}`);
                },
            },
            id
        );
    }

    function getDownloadLabel(execution: any, filesType: string): string {
        let execFilesInfo;
        if (filesType === EFilesType.UPLOADS) {
            execFilesInfo = {
                numberOfFiles: execution.numberOfUploadedFiles,
                hasDeletedFiles: execution.hasDeletedUploadFiles,
            };
        } else {
            execFilesInfo = {
                numberOfFiles: execution.numberOfResultedFiles,
                hasDeletedFiles: execution.hasDeletedResultFiles,
            };
        }

        if (execFilesInfo.hasDeletedFiles) {
            return t('cleanupConfiguration.filesDeleted');
        }
        if (!execFilesInfo.numberOfFiles || execFilesInfo.numberOfFiles < 1) {
            return t('cleanupConfiguration.noFiles');
        }

        return '';
    }

    if (loading) {
        return <Text p="xl">Loading...</Text>;
    }

    if (!execution) {
        return <Text p="xl">Execution not found.</Text>;
    }

    const canCancel = [ERequestStatus.RUNNING, ERequestStatus.NEW].includes(execution.status);
    const canDelete =
        [ERequestStatus.ERROR, ERequestStatus.DONE].includes(execution.status) &&
        (!execution.hasDeletedUploadFiles || !execution.hasDeletedResultFiles);

    return (
        <div style={{maxWidth: 800, margin: 'auto', padding: '1rem'}}>
            <Button
                variant="subtle"
                leftSection={<IconArrowLeft size="1rem" />}
                onClick={() => navigate(-1)}
                mb="md"
            >
                Back to executions
            </Button>

            <Card shadow="sm" p="lg" radius="md" withBorder>
                <Group justify="space-between" mb="md">
                    <div>
                        <Title order={3}>{execution.name}</Title>
                        <Text size="sm" c="dimmed">{execution.label}</Text>
                    </div>
                    <ExecutionStatusBadge status={execution.status} errorMessage={execution.errorMessage} />
                </Group>

                <SimpleGrid cols={{base: 1, sm: 2}} spacing="md">
                    <div>
                        <Text size="xs" c="dimmed">ID</Text>
                        <Group gap="xs">
                            <Text size="sm" style={{wordBreak: 'break-all'}}>{execution.id || execution._id}</Text>
                            <Button size="compact-xs" variant="subtle" p={0} onClick={copyId}>
                                <IconCopy size="0.8rem" />
                            </Button>
                        </Group>
                    </div>
                    <div>
                        <Text size="xs" c="dimmed">Type / Simulator</Text>
                        <Text size="sm">{execution.type || '-'}</Text>
                    </div>
                    <div>
                        <Text size="xs" c="dimmed">Creation Date</Text>
                        <Text size="sm">{execution.creationDate ? formatDate(new Date(execution.creationDate), ELocalesFormats.FR) : '-'}</Text>
                    </div>
                    <div>
                        <Text size="xs" c="dimmed">Running Date</Text>
                        <Text size="sm">{execution.runningDate ? formatDate(new Date(execution.runningDate), ELocalesFormats.FR) : '-'}</Text>
                    </div>
                    <div>
                        <Text size="xs" c="dimmed">Done Date</Text>
                        <Text size="sm">{execution.doneDate ? formatDate(new Date(execution.doneDate), ELocalesFormats.FR) : '-'}</Text>
                    </div>
                    <div>
                        <Text size="xs" c="dimmed">Duration</Text>
                        <Text size="sm">{formatDuration(execution.durationSeconds)}</Text>
                    </div>
                    <div>
                        <Text size="xs" c="dimmed">Executor</Text>
                        <Text size="sm">{execution.agentReference || '-'}</Text>
                    </div>
                    {showUserInfo && execution.user && (
                        <div>
                            <Text size="xs" c="dimmed">User</Text>
                            <Text size="sm">{`${execution.user.firstName ?? ''} ${execution.user.lastName ?? ''}`}</Text>
                            <Text size="xs" c="dimmed">{execution.user.email ?? ''}</Text>
                        </div>
                    )}
                    {showOrgInfo && execution.organization && (
                        <div>
                            <Text size="xs" c="dimmed">Organization</Text>
                            <Text size="sm">{execution.organization.name ?? ''}</Text>
                        </div>
                    )}
                </SimpleGrid>

                {execution.status === ERequestStatus.ERROR && execution.errorMessage && (
                    <Alert icon={<IconAlertCircle size="1rem" />} title="Error" color="red" mt="md">
                        {execution.errorMessage}
                    </Alert>
                )}

                <Title order={5} mt="lg" mb="sm">Files</Title>
                <Group gap="md">
                    <ExecutionDownloadButton
                        label={getDownloadLabel(execution, EFilesType.UPLOADS)}
                        nbFiles={execution.numberOfUploadedFiles}
                        size={execution.weightOfUploadedFiles}
                        isDisabled={!execution.canDownloadUploadFiles}
                        showMetadata={execution.showUploadedFilesMetadata}
                        cleanUpDate={undefined}
                        onClick={e => {
                            e.preventDefault();
                            downloadClicked(execution.id || execution._id, 0, EFilesType.UPLOADS);
                        }}
                    />
                    <ExecutionDownloadButton
                        label={getDownloadLabel(execution, EFilesType.RESULTS)}
                        nbFiles={execution.numberOfResultedFiles}
                        size={execution.weightOfResultedFiles}
                        isDisabled={!execution.canDownloadResultFiles}
                        showMetadata={execution.showResultedFilesMetadata}
                        cleanUpDate={undefined}
                        onClick={e => {
                            e.preventDefault();
                            downloadClicked(execution.id || execution._id, 0, EFilesType.RESULTS);
                        }}
                    />
                </Group>

                <Group mt="lg" gap="md">
                    <Button
                        color="blue"
                        leftSection={<IconCopy size="1rem" />}
                        onClick={copyId}
                    >
                        Copy ID
                    </Button>
                    {userProfile.isAdmin && !execution.hasDeletedUploadFiles && execution.numberOfUploadedFiles > 0 && (
                        <Button
                            color="teal"
                            leftSection={<IconRefresh size="1rem" />}
                            onClick={rerunExecution}
                        >
                            {t('execution.rerun')}
                        </Button>
                    )}
                    {canCancel && (
                        <Button
                            color="orange"
                            leftSection={<IconSquareRoundedX size="1rem" />}
                            onClick={cancelExecution}
                        >
                            Cancel Execution
                        </Button>
                    )}
                    {canDelete && (
                        <Button
                            color="red"
                            leftSection={<IconTrash size="1rem" />}
                            onClick={deleteFiles}
                        >
                            Delete Files
                        </Button>
                    )}
                </Group>
            </Card>
        </div>
    );
}

export default ExecutionDetail;
